import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddSubscribers from "@WhiteCap/Components/OSNSubscriptions/AddSubscribers";
import ManageSubscribers from "@WhiteCap/Components/OSNSubscriptions/ManageSubscribers";
import applySoaSubscriptions from "@WhiteCap/Handlers/SubscriptionsDashboard/SoaSubscriptions/ApplySoaSubscriptions";
import loadSoaSubscribers from "@WhiteCap/Handlers/SubscriptionsDashboard/SoaSubscriptions/GetSoaSubscribers";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import {
    SoaOrderStatusSummaryModel,
    SubscribedEmailDto,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import isEqual from "lodash/isEqual";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: SubscriptionsModalStyles;
    isModalOpen: boolean;
    closeModalHandler: (value: boolean) => void;
    modalTitle: string;
    changeCtaBtnPosition: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSubscriptionCustomerId = state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId;
    const subscriptionsData =
        state.WhiteCap.subscriptionsDashboardData.osnType === "accountLevel"
            ? state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions
            : state.WhiteCap.subscriptionsDashboardData.jobSiteSubscriptionsById[currentSubscriptionCustomerId];
    const subscribersCount = subscriptionsData.soaOrderStatusSummary?.count;
    const emails = subscriptionsData.soaOrderStatusSummary?.subscribedEmails;

    return {
        subscribersCount,
        emails,
        subscriptionsData,
        notificationType: state.WhiteCap.subscriptionsDashboardData.osnType,
        currentSubscriptionCustomerId: state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId,
        isApplyingSubscriptionsChanges: state.WhiteCap.subscriptionsDashboardData.isApplyingSubscriptionsChanges,
    };
};

const mapDispatchToProps = {
    loadSoaSubscribers,
    applySoaSubscriptions,
};

export interface SubscriptionsModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    body?: InjectableCss;
    radioGroup?: RadioGroupProps;
    radioButton?: RadioProps;
    footer?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    proceedButton?: ButtonPresentationProps;
    gridItem?: GridItemProps;
    sectionHeading?: TypographyProps;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
}

export const subscriptionsModalStyles: SubscriptionsModalStyles = {
    modal: {
        size: 1161,
        cssOverrides: {
            modalTitle: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 18px 15px;
                            `,
                            null,
                            css`
                                padding: 28px 33px;
                            `,
                        ],
                        "min",
                    );
                }}
                [class^="TypographyStyle"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                    line-height: 25px;
                                `,
                                null,
                                css`
                                    font-size: 20px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
            `,
            modalContent: css`
                padding: 0;
                display: flex;
                flex-direction: column;
                height: -webkit-fill-available;
                flex-grow: 1;
                overflow: hidden;
            `,
            modalContainer: css`
                [class^="ContentBody"] {
                    overflow: hidden;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    margin: 0 45px;
                                    height: 90vh;
                                    max-height: 846px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
            `,
        },
    },
    body: {
        css: css`
            flex-grow: 1;
            overflow: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 24px 18px;
                        `,

                        css`
                            padding: 17px 30px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    container: { gap: 20 },

    radioGroup: {
        css: css`
            margin-left: 0 !important;
            display: flex;
            flex-direction: row;
            gap: 30px;
            legend {
                display: none;
            }
            & > ${RadioStyle} {
                label {
                    margin-left: 0;
                    font-weight: 325;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 14px;
                                `,
                                null,
                                css`
                                    font-size: 1rem;
                                `,
                            ],
                            "min",
                        )}
                }
                margin-top: 0px !important;
                input {
                    display: contents;
                }
                input:checked + label::after {
                    content: "";
                    display: block;
                    height: 6px;
                    width: 6px;
                    border-radius: 50%;
                    background: black;
                    position: absolute;
                    left: 5px;
                    top: calc(50% - 3px);
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-left: 0;
                        `,
                        null,
                        css`
                            margin-left: 38px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    radioButton: {
        css: css`
            margin-bottom: 18px;
            input {
                margin-left: 0;
                width: 100%;
            }
        `,
    },
    footer: {
        css: css`
            z-index: 1;
            display: flex;
            gap: 20px;
            box-shadow: 0px -4px 6px 0px rgba(0, 0, 0, 0.1);
            padding: 15px;
            justify-content: center;
            align-items: center;
        `,
    },

    cancelButton: {
        variant: "secondary",
    },
    proceedButton: {
        variant: "primary",
    },
    gridItem: {
        width: 12,
        css: css`
            padding: 0;
            margin: 0;
        `,
    },
    sectionHeading: {
        variant: "h4",
        weight: 325,
        lineHeight: "normal",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            margin-bottom: 8px;
                        `,
                        null,
                        css`
                            font-size: 24px;
                            margin-bottom: 21px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100%;
            display: flex;
            align-items: center;
        `,
    },
};

const styles = subscriptionsModalStyles;

const soaSubscriptionsModal = ({
    emails,
    isModalOpen,
    closeModalHandler,
    subscriptionsData,
    subscribersCount,
    currentSubscriptionCustomerId,
    loadSoaSubscribers,
    isApplyingSubscriptionsChanges,
    changeCtaBtnPosition,
    applySoaSubscriptions,
    modalTitle,
}: Props) => {
    const applyButtonText = siteMessage("OSN_Subscriptions_Modal_Apply_Btn_Text");
    const cancelButtonText = siteMessage("OSN_Subscriptions_Modal_Cancel_Btn_Text");
    // State created to compare if something is changed by user in popup.
    const [dataFromAPI, setDataFromAPI] = useState<SoaOrderStatusSummaryModel>();
    const dispatch = useDispatch();

    const printPriceChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/SoaSubscriptions/UpdatePrintPrice",
            printPrice: event.currentTarget.value === "yes"
        })
    };
    const toasterContext = useContext(ToasterContext);

    const hasPhoneNumberErrors = !!(
        emails && emails?.filter((emailObj: SubscribedEmailDto) => emailObj.isEditing).length > 0
    );

    const isApplyButtonDisabled = isApplyingSubscriptionsChanges || hasPhoneNumberErrors;

    const handleProceed = () => {
        if (isApplyButtonDisabled) {
            return;
        }
        // Remove isEditing key from email objects
        const emailsToSend = emails?.map(({ isEditing, ...rest }) => rest) || [];

        if (isEqual(dataFromAPI, subscriptionsData.soaOrderStatusSummary)) {
            // Close modal on click of apply if nothing is changed. 
            closeModalHandler(false);
            return;
        }

        applySoaSubscriptions({
            customerId: currentSubscriptionCustomerId,
            isPrintPriceActive: !!subscriptionsData.soaOrderStatusSummary?.isPrintPriceEnabled,
            subscribedEmails: emailsToSend || [],
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
            onSuccess: () => {
                closeModalHandler(false);
            },
        });
    };

    useEffect(() => {
        if (!currentSubscriptionCustomerId) {
            return;
        }
        loadSoaSubscribers({
            customerId: currentSubscriptionCustomerId,
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
            onSuccess(data) {
                setDataFromAPI(data);
            },
        });
    }, []);

    const handleAddSubscriber = (email: string, onSuccessCallback: () => void) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteAddSubscriber",
            result: {
                email,
                phone: "",
                isSubscribedToReceiveSMS: false,
            },
        });
        onSuccessCallback();
    };

    const handleRemoveEmail = (emailId: string) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteRemoveSubscriber",
            value: emailId,
        });
    };

    const handleUpdateSubscriberDetails = (
        email: string,
        phoneNo: string,
        isSubscribedToReceiveSMS: boolean,
        id?: string,
        isActive?: boolean,
        isEditing?: boolean,
    ) => {
        const subscribedEmailDto: SubscribedEmailDto = {
            id,
            email,
            phone: phoneNo,
            isSubscribedToReceiveSMS,
            isActive,
            isEditing,
        };
        dispatch({
            type: "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteUpdatingSubscriber",
            result: {
                customerId: currentSubscriptionCustomerId,
                subscribedEmailDto,
            },
        });
    };

    const handleRemovePhoneNumber = (emailId: string, onRemovePhoneSuccessCallback: () => void) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteRemovePhoneNumber",
            result: {
                emailId,
            },
        });
        onRemovePhoneSuccessCallback();
    };

    return (
        <Modal
            {...styles.modal}
            headline={modalTitle}
            isOpen={isModalOpen}
            handleClose={() => closeModalHandler(false)}
        >
            <StyledWrapper {...styles.body}>
                <LoadingOverlay {...styles.loadingOverlay} loading={isApplyingSubscriptionsChanges}>
                    <Typography {...styles.sectionHeading}>Print Price (applies to all subscribers)</Typography>
                    <RadioGroup
                        {...styles.radioGroup}
                        value={subscriptionsData.soaOrderStatusSummary?.isPrintPriceEnabled ? "yes" : "no"}
                        onChangeHandler={printPriceChangeHandler}
                    >
                        <Radio {...styles.radioButton} value="yes">
                            {translate("Yes")}
                        </Radio>
                        <Radio {...styles.radioButton} value="no">
                            {translate("No")}
                        </Radio>
                    </RadioGroup>
                    <AddSubscribers
                        addSubscriber={handleAddSubscriber}
                        emails={emails}
                        subscriptionModalType="soaOrderStatusSummary"
                    />
                    <ManageSubscribers
                        subscribersCount={subscribersCount}
                        emails={emails || []}
                        removeEmail={handleRemoveEmail}
                        updateSubscriberDetails={handleUpdateSubscriberDetails}
                        removePhoneNumber={handleRemovePhoneNumber}
                    />
                </LoadingOverlay>
            </StyledWrapper>
            <StyledWrapper {...styles.footer}>
                {changeCtaBtnPosition ? (
                    <>
                        <Button {...styles.proceedButton} onClick={handleProceed} disabled={isApplyButtonDisabled}>
                            {applyButtonText}
                        </Button>
                        <Button
                            {...styles.cancelButton}
                            onClick={() => closeModalHandler(false)}
                            data-test-selector="cancelButton"
                        >
                            {cancelButtonText}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            {...styles.cancelButton}
                            onClick={() => closeModalHandler(false)}
                            data-test-selector="cancelButton"
                        >
                            {cancelButtonText}
                        </Button>
                        <Button {...styles.proceedButton} onClick={handleProceed} disabled={isApplyButtonDisabled}>
                            {applyButtonText}
                        </Button>
                    </>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(soaSubscriptionsModal);
